import "@/i18n/i18n";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CountryProvider } from "@/contexts/CountryContext";
import { ReadingProvider } from "@/contexts/ReadingContext";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Historial from "@/pages/Historial";
import AuthCallback from "@/pages/AuthCallback";
import SharedRecommendation from "@/pages/SharedRecommendation";
import Leidos from "@/pages/Leidos";
import Marketplace from "@/pages/Marketplace";
import Listing from "@/pages/Listing";
import MyOrders from "@/pages/MyOrders";
import Sell from "@/pages/Sell";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Terms from "@/pages/Terms";
import Ratings from "@/pages/Ratings";
import Clubs from "@/pages/Clubs";
import ClubDetail from "@/pages/ClubDetail";
import ClubCreate from "@/pages/ClubCreate";
import ClubSummary from "@/pages/ClubSummary";
import MyClubs from "@/pages/MyClubs";
import AdminInsights from "@/pages/AdminInsights";
import AdminCrud from "@/pages/AdminCrud";
import AdminOnly from "@/components/AdminOnly";
import MarketplaceComingSoon from "@/pages/MarketplaceComingSoon";
import ClubsPrivateLanding from "@/pages/ClubsPrivateLanding";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import Unsubscribe from "@/pages/Unsubscribe";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import LibroPage from "@/pages/LibroPage";
import AdminBlog from "@/pages/AdminBlog";
import Footer from "@/components/Footer";

function AppRouter() {
    const location = useLocation();
    const { i18n, t } = useTranslation();

    // Sync <html lang=...> + browser tab title with active language for
    // screen readers and automatic translation tools.
    useEffect(() => {
        const lang = (i18n.language || "en").split("-")[0];
        document.documentElement.setAttribute("lang", lang);
        document.title = t("nav.brand") + " · " + t("home.title_line1") + " " + t("home.title_line2");
    }, [i18n.language, t]);

    // Expose a global click-tracking helper used by BookCard and the local
    // bookshop CTA. Fire-and-forget; never blocks navigation.
    useEffect(() => {
        window.recomendalibros = window.recomendalibros || {};
        window.recomendalibros.trackClick = (payload) => {
            try {
                api.post("/insights/click", payload).catch(() => {});
            } catch { /* ignore */ }
        };
    }, []);

    // Anonymous pageview tracking — one event per route change. Best-effort.
    useEffect(() => {
        if (!location?.pathname) return;
        try {
            api.post("/insights/pageview", {
                path: location.pathname,
                referrer: document.referrer || null,
            }).catch(() => {});
        } catch { /* ignore */ }
    }, [location.pathname]);

    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <Nav />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/acceder" element={<Login />} />
                <Route path="/registrarse" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/biblioteca" element={<Historial />} />
                <Route path="/leidos" element={<Leidos />} />
                <Route path="/marketplace" element={<MarketplaceComingSoon />} />
                <Route path="/marketplace/orders" element={<AdminOnly><MyOrders /></AdminOnly>} />
                <Route path="/marketplace/admin" element={<AdminOnly><Marketplace /></AdminOnly>} />
                <Route path="/marketplace/:listingId" element={<AdminOnly><Listing /></AdminOnly>} />
                <Route path="/sell" element={<AdminOnly><Sell /></AdminOnly>} />
                <Route path="/payment/success" element={<AdminOnly><PaymentSuccess /></AdminOnly>} />
                <Route path="/terminos" element={<AdminOnly><Terms /></AdminOnly>} />
                <Route path="/valoraciones" element={<Ratings />} />
                <Route path="/clubs" element={<AdminOnly fallback="/clubs-private"><Clubs /></AdminOnly>} />
                <Route path="/clubs-private" element={<ClubsPrivateLanding />} />
                <Route path="/clubs/new" element={<AdminOnly fallback="/clubs-private"><ClubCreate /></AdminOnly>} />
                <Route path="/clubs/me" element={<AdminOnly fallback="/clubs-private"><MyClubs /></AdminOnly>} />
                <Route path="/clubs/:slug" element={<AdminOnly fallback="/clubs-private"><ClubDetail /></AdminOnly>} />
                <Route path="/clubs/:slug/cycles/:cycleId/summary" element={<ClubSummary />} />
                <Route path="/r/:id" element={<SharedRecommendation />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/libro" element={<LibroPage />} />
                <Route path="/admin" element={<AdminInsights />} />
                <Route path="/admin/manage" element={<AdminCrud />} />
                <Route path="/admin/blog" element={<AdminOnly><AdminBlog /></AdminOnly>} />
                <Route path="/privacidad" element={<Privacy />} />
                <Route path="/contacto" element={<Contact />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
            </Routes>
            <Footer />
        </>
    );
}

function App() {
    return (
        <div className="App font-body">
            <HelmetProvider>
                <CountryProvider>
                    <AuthProvider>
                        <ReadingProvider>
                            <BrowserRouter>
                                <AppRouter />
                                <Toaster
                                    position="bottom-right"
                                    toastOptions={{
                                        style: {
                                            background: "#16140f",
                                            color: "#f1e8d6",
                                            border: "1px solid #3d3730",
                                            fontFamily: "Roboto Flex, sans-serif",
                                        },
                                    }}
                                />
                            </BrowserRouter>
                        </ReadingProvider>
                    </AuthProvider>
                </CountryProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
